import { useEffect, useMemo, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import {
  SongDetailActionLink,
  SongDetailActions,
  SongDetailBody,
  SongDetailCard,
  SongDetailGrid,
  SongDetailItem,
  SongDetailLabel,
  SongDetailLayout,
  SongDetailMeta,
  SongDetailTitle,
  SongDetailValue,
} from '../components/styled';
import { useLibraryApi, useBand } from '../context/BandContext';
import { api } from '../lib/api';

function SongViewPage() {
  const { songId = '', bandId = '', userId = '' } = useParams();
  const defaultLibraryApi = useLibraryApi();
  const userLibraryApi = useMemo(() => userId ? api.forUser(userId) : null, [userId]);
  const libraryApi = userLibraryApi || defaultLibraryApi;
  const bandContext = useBand();
  const pathPrefix = bandId ? `/bands/${bandId}` : userId ? `/users/${userId}` : '';
  const [searchParams] = useSearchParams();
  const [song, setSong] = useState(null);
  const [albumTitles, setAlbumTitles] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState('');
  const albumId = searchParams.get('albumId') || '';
  const sourceQuery = searchParams.get('q') || '';
  const sourceAlphaRange = searchParams.get('alpha') || '';
  const sourceSortBy = searchParams.get('sort') || '';
  const songSearchParams = new URLSearchParams();

  if (albumId) {
    songSearchParams.set('albumId', albumId);
  }

  if (sourceQuery) {
    songSearchParams.set('q', sourceQuery);
  }

  if (sourceAlphaRange) {
    songSearchParams.set('alpha', sourceAlphaRange);
  }

  if (sourceSortBy) {
    songSearchParams.set('sort', sourceSortBy);
  }

  const songSearch = songSearchParams.toString()
    ? `?${songSearchParams.toString()}`
    : '';
  const librarySearchParams = new URLSearchParams();

  if (sourceQuery) {
    librarySearchParams.set('q', sourceQuery);
  }

  if (sourceAlphaRange) {
    librarySearchParams.set('alpha', sourceAlphaRange);
  }

  if (sourceSortBy) {
    librarySearchParams.set('sort', sourceSortBy);
  }

  if (userId) {
    librarySearchParams.set('scope', 'user');
    librarySearchParams.set('userId', userId);
  }

  if (bandId) {
    librarySearchParams.set('scope', 'band');
    librarySearchParams.set('bandId', bandId);
  }

  const librarySearch = librarySearchParams.toString()
    ? `?${librarySearchParams.toString()}`
    : '';
  const backTo = userId
    ? `/library/songs${librarySearch}`
    : albumId
    ? `/library/albums/${albumId}${librarySearch}`
    : `/library/songs${librarySearch}`;
  const backLabel = albumId ? 'Back to Album' : 'Back to Library';

  useEffect(() => {
    let isActive = true;

    const loadSong = async () => {
      setIsLoading(true);
      setErrorMessage('');

      try {
        const [songResponse, albumsResponse] = await Promise.all([
          libraryApi.getSong(songId),
          libraryApi.getAlbums(),
        ]);

        if (!isActive) {
          return;
        }

        const nextSong = songResponse?.song || null;
        const matchingAlbums = (albumsResponse?.albums || [])
          .filter((album) =>
            (album.songs || []).some((currentSong) => currentSong._id === songId)
          )
          .map((album) => album.title || 'Untitled album');

        setSong(nextSong);
        setAlbumTitles(matchingAlbums);
      } catch (error) {
        if (!isActive) {
          return;
        }

        setSong(null);
        setAlbumTitles([]);

        if (error.status !== 401) {
          setErrorMessage(error.message || 'Failed to load song.');
        }
      } finally {
        if (isActive) {
          setIsLoading(false);
        }
      }
    };

    loadSong();

    return () => {
      isActive = false;
    };
  }, [songId, libraryApi]);

  const pageCount = song?.pages?.length || 0;
  const artistLabel = song?.artist || 'Unknown artist';
  const albumLabel =
    albumTitles.length > 0 ? albumTitles.join(', ') : 'No albums assigned';

  return (
    <SongDetailLayout>
      <SongDetailCard>
        {isLoading ? (
          <>
            <SongDetailMeta>Loading</SongDetailMeta>
            <SongDetailTitle>Fetching song from the API...</SongDetailTitle>
          </>
        ) : null}

        {!isLoading && errorMessage ? (
          <>
            <SongDetailMeta>Error</SongDetailMeta>
            <SongDetailTitle>Song unavailable</SongDetailTitle>
            <SongDetailBody>{errorMessage}</SongDetailBody>
          </>
        ) : null}

        {!isLoading && !errorMessage && song ? (
          <>
            <SongDetailMeta>{artistLabel}</SongDetailMeta>
            <SongDetailTitle>{song.title || 'Untitled song'}</SongDetailTitle>
            <SongDetailBody>Choose how you want to open this song.</SongDetailBody>
            <SongDetailGrid>
              <SongDetailItem>
                <SongDetailLabel>Albums</SongDetailLabel>
                <SongDetailValue>{albumLabel}</SongDetailValue>
              </SongDetailItem>
              <SongDetailItem>
                <SongDetailLabel>Pages</SongDetailLabel>
                <SongDetailValue>{pageCount}</SongDetailValue>
              </SongDetailItem>
              <SongDetailItem>
                <SongDetailLabel>Played Count</SongDetailLabel>
                <SongDetailValue>{song.playedCount || 0}</SongDetailValue>
              </SongDetailItem>
              <SongDetailItem>
                <SongDetailLabel>Flag</SongDetailLabel>
                <SongDetailValue>{song.flag ? 'Flagged' : 'Normal'}</SongDetailValue>
              </SongDetailItem>
            </SongDetailGrid>
          </>
        ) : null}
      </SongDetailCard>

      <SongDetailActions>
        <SongDetailActionLink as={Link} to={backTo}>
          {backLabel}
        </SongDetailActionLink>
        {!isLoading && !errorMessage && song ? (
          <SongDetailActionLink
            as={Link}
            to={`${pathPrefix}/song/${song._id}/view${songSearch}`}
          >
            Page View
          </SongDetailActionLink>
        ) : null}
        {!isLoading && !errorMessage && song && !userId && (!bandContext || bandContext.band.canManage) ? (
          <SongDetailActionLink as={Link} to={`${pathPrefix}/songs/${song._id}/edit${songSearch}`}>
            Edit Song
          </SongDetailActionLink>
        ) : null}
      </SongDetailActions>
    </SongDetailLayout>
  );
}

export default SongViewPage;
